/**
 * Authentication Context
 *
 * Provider de autenticação para toda a aplicação React.
 * Gerencia estado de autenticação, login, logout e MFA.
 * Inclui refresh automático de token e tratamento de sessão expirada.
 *
 * @since 2025-12-15
 * @updated 2025-12-18 - Adicionado listener para sessão expirada
 * @epic INIT-0045-03, INIT-0046
 */

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from 'react';
import { useNavigate, Navigate, useLocation } from 'react-router-dom';
import {
  authApi,
  AuthUser,
  LoginCredentials,
  MFACredentials,
  LoginResponse,
} from '@/services/authApi';
import { getJwtToken, clearTokens } from '@/services/apiClient';
import { BootLoader } from '@/components/ui/BootLoader';

// ==========================================
// SECURITY: URL Validation (CVE-2026-22029)
// ==========================================

/**
 * Valida se uma URL de retorno é segura para redirecionamento.
 * Previne ataques de Open Redirect e XSS via javascript: URLs.
 *
 * @param url - URL a ser validada
 * @returns true se a URL é segura (path relativo interno)
 */
function isValidReturnUrl(url: string | null): boolean {
  if (!url) return false;

  // Deve começar com / (path relativo)
  if (!url.startsWith('/')) return false;

  // Bloqueia redirect para a própria página de login (causa loop infinito)
  if (url.startsWith('/login')) return false;

  // Bloqueia URLs absolutas disfarçadas (//example.com)
  if (url.startsWith('//')) return false;

  // Bloqueia protocol handlers (javascript:, data:, vbscript:, etc.)
  if (url.includes(':')) return false;

  // Bloqueia caracteres de controle e encoding malicioso
  // eslint-disable-next-line no-control-regex -- intencionalmente detectando caracteres de controle
  if (/[\x00-\x1f\x7f]/.test(url)) return false;

  return true;
}

/**
 * Retorna uma URL de retorno segura, com fallback para URL padrão.
 *
 * @param url - URL candidata
 * @param fallback - URL padrão se a candidata for inválida
 * @returns URL segura para redirecionamento
 */
function getSafeReturnUrl(url: string | null, fallback: string): string {
  return isValidReturnUrl(url) ? url! : fallback;
}

// ==========================================
// TYPES
// ==========================================

export interface AuthContextValue {
  // Estado
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;

  // Propriedades derivadas do usuário
  clientCode: string | null;
  isAdmin: boolean;

  // MFA
  requireMFA: boolean;
  mfaSessionToken: string | null;

  // Ações
  login: (credentials: LoginCredentials) => Promise<LoginResponse>;
  loginMFA: (credentials: MFACredentials) => Promise<LoginResponse>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  clearError: () => void;

  // Helpers
  hasRole: (role: string) => boolean;
  hasPermission: (permission: string) => boolean;
}

// ==========================================
// CONTEXT
// ==========================================

const AuthContext = createContext<AuthContextValue | null>(null);

// ==========================================
// PROVIDER
// ==========================================

interface AuthProviderProps {
  children: ReactNode;
  redirectTo?: string; // URL para redirecionar após login
  loginPath?: string; // Path da página de login
}

export function AuthProvider({
  children,
  redirectTo = '/',
  loginPath = '/login',
}: AuthProviderProps) {
  const navigate = useNavigate();

  // Estado
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [requireMFA, setRequireMFA] = useState(false);
  const [mfaSessionToken, setMfaSessionToken] = useState<string | null>(null);

  // Derived state
  const isAuthenticated = !!user;

  // ==========================================
  // INIT: Verificar autenticação ao montar
  // ==========================================

  useEffect(() => {
    const initAuth = async () => {
      // Verificar se há token válido no localStorage
      const token = getJwtToken();
      if (!token) {
        // Sem token válido, não precisa verificar
        setUser(null);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      try {
        const currentUser = await authApi.getCurrentUser();
        if (currentUser) {
          setUser(currentUser);
        } else {
          // Token inválido - limpar
          clearTokens();
          setUser(null);
        }
      } catch {
        // Erro ao verificar - limpar tokens inválidos
        clearTokens();
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    initAuth();
  }, []);

  // ==========================================
  // SESSION EXPIRED: Listener para token expirado
  // ==========================================

  useEffect(() => {
    const handleSessionExpired = () => {
      if (import.meta.env.DEV) console.debug('[Auth] Session expired, redirecting to login...');
      setUser(null);
      setRequireMFA(false);
      setMfaSessionToken(null);
      setError('Sua sessão expirou. Por favor, faça login novamente.');

      // Salvar URL atual para retornar após login
      const returnUrl = encodeURIComponent(
        window.location.pathname + window.location.search
      );
      navigate(`${loginPath}?returnUrl=${returnUrl}`);
    };

    // Escuta evento disparado pelo apiClient quando refresh falha
    window.addEventListener('auth:session-expired', handleSessionExpired);

    return () => {
      window.removeEventListener('auth:session-expired', handleSessionExpired);
    };
  }, [navigate, loginPath]);

  // ==========================================
  // LOGIN
  // ==========================================

  const login = useCallback(
    async (credentials: LoginCredentials): Promise<LoginResponse> => {
      setIsLoading(true);
      setError(null);
      setRequireMFA(false);
      setMfaSessionToken(null);

      try {
        const response = await authApi.login(credentials);

        if (response.success) {
          if (response.requireMFA) {
            // MFA necessário - não redireciona ainda
            setRequireMFA(true);
            setMfaSessionToken(response.mfaSessionToken || null);
          } else if (response.user) {
            // Login completo
            setUser(response.user);

            // Redirecionar (com validação de segurança - CVE-2026-22029)
            const returnUrl = getSafeReturnUrl(
              new URLSearchParams(window.location.search).get('returnUrl'),
              redirectTo
            );
            navigate(returnUrl);
          }
        } else {
          setError(response.message || 'Erro ao fazer login');
        }

        return response;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Erro ao fazer login';
        setError(message);
        return { success: false, message };
      } finally {
        setIsLoading(false);
      }
    },
    [navigate, redirectTo]
  );

  // ==========================================
  // LOGIN MFA
  // ==========================================

  const loginMFA = useCallback(
    async (credentials: MFACredentials): Promise<LoginResponse> => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await authApi.loginMFA({
          ...credentials,
          sessionToken: mfaSessionToken || undefined,
        });

        if (response.success && response.user) {
          setUser(response.user);
          setRequireMFA(false);
          setMfaSessionToken(null);

          // Redirecionar (com validação de segurança - CVE-2026-22029)
          const returnUrl = getSafeReturnUrl(
            new URLSearchParams(window.location.search).get('returnUrl'),
            redirectTo
          );
          navigate(returnUrl);
        } else {
          setError(response.message || 'Código MFA inválido');
        }

        return response;
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Erro ao verificar código MFA';
        setError(message);
        return { success: false, message };
      } finally {
        setIsLoading(false);
      }
    },
    [navigate, redirectTo, mfaSessionToken]
  );

  // ==========================================
  // LOGOUT
  // ==========================================

  const logout = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    try {
      await authApi.logout();
    } finally {
      setUser(null);
      setRequireMFA(false);
      setMfaSessionToken(null);
      setIsLoading(false);

      // Redirecionar para login usando React Router
      navigate(loginPath);
    }
  }, [navigate, loginPath]);

  // ==========================================
  // REFRESH USER
  // ==========================================

  const refreshUser = useCallback(async (): Promise<void> => {
    try {
      const currentUser = await authApi.getCurrentUser();
      setUser(currentUser);
    } catch {
      setUser(null);
    }
  }, []);

  // ==========================================
  // HELPERS
  // ==========================================

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const hasRole = useCallback(
    (role: string): boolean => {
      return user?.roles?.includes(role) ?? false;
    },
    [user]
  );

  const hasPermission = useCallback(
    (permission: string): boolean => {
      return user?.permissions?.includes(permission) ?? false;
    },
    [user]
  );

  // ==========================================
  // VALUE
  // ==========================================

  // Propriedades derivadas do usuário
  const clientCode = user?.siteId?.toString() || user?.idSite?.toString() || null;
  const isAdmin = user?.roles?.includes('admin') ?? false;

  const value: AuthContextValue = {
    user,
    isAuthenticated,
    isLoading,
    error,
    clientCode,
    isAdmin,
    requireMFA,
    mfaSessionToken,
    login,
    loginMFA,
    logout,
    refreshUser,
    clearError,
    hasRole,
    hasPermission,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// ==========================================
// HOOK
// ==========================================

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  return context;
}

// ==========================================
// PROTECTED ROUTE COMPONENT
// ==========================================

interface ProtectedRouteProps {
  children: ReactNode;
  requiredRole?: string;
  requiredPermission?: string;
  fallback?: ReactNode;
}

export function ProtectedRoute({
  children,
  requiredRole,
  requiredPermission,
  fallback,
}: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, hasRole, hasPermission } = useAuth();
  const location = useLocation();

  // Loading state - mesma tela dos demais estágios do boot.
  // Antes este ramo não pintava fundo algum e o body branco aparecia entre o
  // loader do Suspense e a aplicação — o flash claro do boot [TMF9-61077].
  if (isLoading) {
    return fallback || <BootLoader />;
  }

  // Not authenticated - redirect to login immediately (synchronous)
  if (!isAuthenticated) {
    const returnUrl = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?returnUrl=${returnUrl}`} replace />;
  }

  // Verificar role
  if (requiredRole && !hasRole(requiredRole)) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600">Acesso Negado</h1>
          <p className="text-gray-600 mt-2">
            Você não tem permissão para acessar esta página.
          </p>
        </div>
      </div>
    );
  }

  // Verificar permission
  if (requiredPermission && !hasPermission(requiredPermission)) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600">Acesso Negado</h1>
          <p className="text-gray-600 mt-2">
            Você não tem permissão para acessar esta página.
          </p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}

// ==========================================
// EXPORTS
// ==========================================

export default AuthContext;
